// Personalized video message shown on Glow Up Gap result pages (both the
// in-flow results screen and the shareable report page). Mobile-first, portrait
// aspect preserved, centered with generous white space on desktop.
//
// Design notes:
// - Autoplays MUTED on load (browser policy). User can unmute via native controls.
// - No loop. Standard play/pause + volume controls visible via `controls`.
// - Wrapper uses TOKENS (matching the rest of the assessment surface) so it
//   feels editorial rather than like an embedded iframe.
// - Renders nothing (returns null) when no matching video URL exists — the rest
//   of the page renders without a hole per the spec.
//
// Analytics (fires ONCE per session — refs prevent duplicates):
// - `GlowUpGapVideoPlay`     → Meta Pixel + backend beacon on unmute/play
// - `GlowUpGapVideoComplete` → Meta Pixel + backend beacon at 90% watched
// - `pct` progress bumped opportunistically via `timeupdate` so max watched is captured
import { useEffect, useRef } from 'react';
import { GAP_TITLES, GAP_VIDEOS } from './glowUpGapNarratives';
import { trackCustomEvent } from '@/utils/metaPixel';

const API = `${window.location.origin}/api`;

// Beacon helper — falls back to fetch() with keepalive if sendBeacon unavailable.
// Both APIs are fire-and-forget by design; user can navigate/close mid-request safely.
function sendVideoEvent(payload) {
  try {
    const url = `${API}/glow-up-gap/video-event`;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
  } catch (_) {
    /* swallow — analytics must never break UX */
  }
}

export default function GlowUpGapVideoSection({ primaryGap, tokens, reportId }) {
  const src = GAP_VIDEOS[primaryGap];
  const videoRef = useRef(null);
  const playedRef = useRef(false);
  const completedRef = useRef(false);
  const lastFlushedPctRef = useRef(0);

  useEffect(() => {
    // Reset event guards when gap or report changes (viewing different reports in same session)
    playedRef.current = false;
    completedRef.current = false;
    lastFlushedPctRef.current = 0;
  }, [primaryGap, reportId]);

  if (!src) return null;

  const T = tokens || {};
  const gapLabel = GAP_TITLES[primaryGap] || '';

  const firePlay = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    const pct = computePct();
    try {
      trackCustomEvent('GlowUpGapVideoPlay', {
        content_name: 'Glow Up Gap Personalized Video',
        primary_gap: primaryGap,
        pct,
      });
    } catch (_) { /* ignore */ }
    if (reportId) {
      sendVideoEvent({ report_id: reportId, event_type: 'play', pct });
    }
  };

  const fireComplete = (pct) => {
    if (completedRef.current) return;
    completedRef.current = true;
    try {
      trackCustomEvent('GlowUpGapVideoComplete', {
        content_name: 'Glow Up Gap Personalized Video',
        primary_gap: primaryGap,
        pct,
      });
    } catch (_) { /* ignore */ }
    if (reportId) {
      sendVideoEvent({ report_id: reportId, event_type: 'complete', pct });
    }
  };

  const computePct = () => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return 0;
    return Math.round((v.currentTime / v.duration) * 100);
  };

  // `play` event fires from autoplay too — but we want the analytics play to only count
  // once the user is genuinely engaged. We treat "unmute" OR "manual play after pause" as the
  // real signal. Autoplay-muted alone is ignored.
  const handleVolumeChange = () => {
    if (!videoRef.current) return;
    if (!videoRef.current.muted && !playedRef.current) firePlay();
  };
  const handlePlay = () => {
    // Manual play (after pause) also counts. Skip if still muted AND at time=0 (that's the autoplay-muted moment).
    if (!videoRef.current) return;
    const v = videoRef.current;
    if (v.muted && v.currentTime < 0.5) return;
    firePlay();
  };
  const handleTimeUpdate = () => {
    const pct = computePct();
    if (!completedRef.current && pct >= 90) fireComplete(pct);
    // Opportunistically push max-watched-pct on the way through so we capture it even
    // if the user closes the tab before hitting 90%. Only send every 15% to avoid flood.
    if (reportId && pct - lastFlushedPctRef.current >= 15) {
      lastFlushedPctRef.current = pct;
      sendVideoEvent({ report_id: reportId, event_type: 'play', pct });
    }
  };
  const handleEnded = () => {
    // Belt-and-suspenders — some browsers fire `ended` before hitting exact 90 in timeupdate.
    if (!completedRef.current) fireComplete(100);
  };

  return (
    <section
      style={{
        background: T.cardBg || '#F9F1E7',
        border: `1px solid ${T.cardBorder || '#EAD8C2'}`,
        borderRadius: '16px',
        padding: '28px 20px',
        margin: '28px 0',
        textAlign: 'center',
        boxShadow: '0 2px 12px rgba(61, 50, 41, 0.05)',
      }}
      data-testid={`glowup-personalized-video-section-${primaryGap}`}
      aria-labelledby="glowup-personal-video-heading"
    >
      <h2
        id="glowup-personal-video-heading"
        style={{
          fontFamily: T.serif || "'Libre Baskerville', serif",
          fontSize: '20px',
          lineHeight: 1.3,
          color: T.dark || '#3D3229',
          margin: '0 0 10px 0',
          letterSpacing: '-0.01em',
        }}
      >
        {'\uD83D\uDC9B'} Before You Go Any Further...
      </h2>
      <p
        style={{
          fontFamily: T.sans || "'DM Sans', sans-serif",
          fontSize: '14px',
          lineHeight: 1.6,
          color: T.muted || '#8B7D73',
          margin: '0 auto 20px',
          maxWidth: '440px',
          fontStyle: 'italic',
        }}
      >
        I recorded this message because I want you to feel supported — not judged.
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: '340px',
          margin: '0 auto',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 6px 24px rgba(61, 50, 41, 0.12)',
          backgroundColor: '#000',
        }}
      >
        <video
          key={src}
          ref={videoRef}
          src={src}
          autoPlay
          muted
          playsInline
          preload="metadata"
          controls
          controlsList="nodownload"
          onVolumeChange={handleVolumeChange}
          onPlay={handlePlay}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          style={{
            width: '100%',
            display: 'block',
            aspectRatio: '9 / 16',
            objectFit: 'cover',
            backgroundColor: '#000',
          }}
          data-testid={`glowup-personalized-video-${primaryGap}`}
          aria-label={`Personal message from Deon about your ${gapLabel} Gap`}
        >
          Your browser doesn&apos;t support video playback. Please open this page in a modern browser to watch Deon&apos;s message.
        </video>
      </div>

      <p
        style={{
          fontFamily: T.sans || "'DM Sans', sans-serif",
          fontSize: '11px',
          lineHeight: 1.5,
          color: T.muted || '#8B7D73',
          margin: '14px 0 0 0',
          opacity: 0.7,
        }}
      >
        Tap the volume icon to unmute
      </p>
    </section>
  );
}
